/** Schedule-template hooks (owner/manager via RLS): list templates for a scope,
 *  save the current period as a template, delete one, and load (apply) one into
 *  the current period. Templates persist in shift_templates and are shared across
 *  the tenant's managers. */
import {
  createShift,
  createShiftTemplate,
  type CreateTemplateInput,
  deleteShiftTemplate,
  listShiftTemplates,
  type ShiftTemplate,
} from '@gym/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../lib/auth.js';
import { supabase } from '../../../lib/supabase.js';

import { type ScheduleView, templateToShiftInputs } from './scheduleUtils.js';

/** Saved templates for the given scope (day/week/month). */
export function useShiftTemplates(scope: ScheduleView) {
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  return useQuery({
    queryKey: ['shift_templates', tenantId, scope],
    enabled: !!tenantId,
    queryFn: () => listShiftTemplates(supabase, tenantId, scope),
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: (input: CreateTemplateInput) =>
      createShiftTemplate(supabase, claims?.tenant_id ?? '', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shift_templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteShiftTemplate(supabase, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shift_templates'] }),
  });
}

/** Apply a template into the period containing `anchor`; returns the count created. */
export function useLoadTemplate() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: async (vars: {
      template: ShiftTemplate;
      anchor: Date;
      validStaffIds: Set<string>;
    }): Promise<number> => {
      const inputs = templateToShiftInputs(
        vars.template.scope,
        vars.anchor,
        vars.template.entries,
        vars.validStaffIds,
      );
      let count = 0;
      for (const input of inputs) {
        const { error } = await createShift(supabase, claims?.tenant_id ?? '', input);
        if (error) throw error;
        count += 1;
      }
      return count;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}
